import { useTranslation } from 'react-i18next'
import { Mail, Phone, User, MapPin, FileText, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ClientAvatar } from '@/components/ClientAvatar'
import type { Appointment, Client } from '@/lib/api'
import { isPastStart, toDateKey } from './calendarHelpers'
import { eventColor } from './constants'

export interface AppointmentDetailPanelProps {
  appt: Appointment
  client?: Client
  onClose: () => void
  onEdit: () => void
  /** 标记完成（仅过期 scheduled 显示） */
  onComplete?: () => void
  /** 标记未出席（仅过期 scheduled 显示，已扣课时退回） */
  onNoShow?: () => void
  /** 更改时间（仅 scheduled 显示，OPT-013：与拖拽同走 /move 直接生效） */
  onMove?: () => void
  /** 硬删除（仅 scheduled 显示，占用课时退回） */
  onDelete?: () => void
}

/**
 * 桌面端浮层详情面板：学员名大标题 + 日期时间 + 状态色点 + 联系方式图标行 + 竖排操作按钮。
 * 按钮可见性规则与 EditApptFormFields 一致（状态 + isPastStart）。
 * OPT-012：取消按钮已移除；OPT-013：scheduled 提供"更改时间"入口（直接生效）；
 * OPT-014：状态色点走 eventColor 单一来源；OPT-019：展示绑定课包名与剩余课时。
 */
export function AppointmentDetailPanel({
  appt,
  client,
  onClose,
  onEdit,
  onComplete,
  onNoShow,
  onMove,
  onDelete,
}: AppointmentDetailPanelProps) {
  const { t } = useTranslation()
  const isScheduled = appt.status === 'scheduled'
  const past = isPastStart(appt)

  return (
    <div className="flex flex-col h-full" data-testid="appt-panel-detail">
      <div className="flex items-start justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 min-w-0">
          {/* 头像 + 名字横排（参考 Loticnature）；无头像时为有色占位 */}
          {client && <ClientAvatar client={client} size={40} />}
          <h2 className="text-lg font-semibold leading-snug truncate">
            {client?.name || appt.title || t('schedule.panel.untitled')}
          </h2>
        </div>
        <button
          type="button"
          data-testid="panel-close"
          aria-label={t('common.close')}
          className="text-slate-400 hover:text-slate-600 text-lg leading-none px-1"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="px-4 pb-3">
        <div className="text-sm text-slate-600">
          {toDateKey(appt.scheduledDate)} {appt.startTime} - {appt.endTime}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
          <span
            data-testid="status-dot"
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: eventColor(appt) }}
          />
          {t(`schedule.status.${appt.status}`)}
        </div>
      </div>

      <div className="border-t mx-4" />

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5 text-sm">
        {client && (
          <div className="flex items-center gap-2.5">
            <User className="h-4 w-4 shrink-0 text-slate-400" />
            <span>{client.name}</span>
          </div>
        )}
        {client?.email && (
          <div className="flex items-center gap-2.5" data-testid="contact-email">
            <Mail className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="break-all">{client.email}</span>
          </div>
        )}
        {client?.phone && (
          <div className="flex items-center gap-2.5" data-testid="contact-phone">
            <Phone className="h-4 w-4 shrink-0 text-slate-400" />
            <span>{client.phone}</span>
          </div>
        )}
        {appt.location && (
          <div className="flex items-center gap-2.5">
            <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
            <span>{appt.location}</span>
          </div>
        )}
        {appt.packageName && (
          <div className="flex items-center gap-2.5" data-testid="panel-package">
            <Package className="h-4 w-4 shrink-0 text-slate-400" />
            <span>
              {appt.packageName}
              {appt.packageRemainingSessions != null &&
                ` · ${t('schedule.panel.remainingSessions', { count: appt.packageRemainingSessions })}`}
            </span>
          </div>
        )}
        {appt.notes && (
          <div className="flex items-start gap-2.5">
            <FileText className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" />
            <span className="whitespace-pre-wrap">{appt.notes}</span>
          </div>
        )}
      </div>

      <div data-testid="panel-actions" className="border-t px-4 py-3 flex flex-col gap-2">
        {isScheduled && past && onComplete && (
          <Button type="button" data-testid="appt-complete" onClick={() => onComplete()}>
            {t('schedule.actions.complete')}
          </Button>
        )}
        {isScheduled && past && onNoShow && (
          <Button
            type="button"
            variant="outline"
            data-testid="appt-noshow"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => {
              if (window.confirm(t('schedule.confirmNoShow'))) onNoShow()
            }}
          >
            {t('schedule.actions.noShow')}
          </Button>
        )}
        {isScheduled && onMove && (
          <Button type="button" variant="outline" data-testid="appt-move" onClick={() => onMove()}>
            {t('schedule.actions.moveTime')}
          </Button>
        )}
        {/* OPT-024：仅 scheduled 可编辑，终态(completed/no_show)只读 */}
        {isScheduled && (
          <Button type="button" variant="outline" data-testid="panel-edit" onClick={onEdit}>
            {t('common.edit')}
          </Button>
        )}
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            data-testid="appt-delete"
            className="text-red-600 hover:bg-red-50"
            onClick={() => {
              if (window.confirm(t('schedule.confirmDelete'))) onDelete()
            }}
          >
            {t('common.delete')}
          </Button>
        )}
      </div>
    </div>
  )
}
